package dev.lingvo.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.core.Page
import dev.lingvo.site.components.marketing.DocEntry
import dev.lingvo.site.components.marketing.AgentSetup
import dev.lingvo.site.components.marketing.AgentValueComparison
import dev.lingvo.site.components.marketing.AgentValueExample
import dev.lingvo.site.components.marketing.AgentValueFit
import dev.lingvo.site.components.marketing.DocsList
import dev.lingvo.site.components.marketing.DocsInlineLink
import dev.lingvo.site.components.marketing.DocsProse
import dev.lingvo.site.components.marketing.MarketingDocPage
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import kotlinx.browser.document

/** Explains Lingvo's incremental value, then connects the client's existing coding agent. */
@Page("/agents")
@Composable
fun AgentsPage() {
    LaunchedEffect(Unit) {
        document.title = tr("agents.coding_agents_lingvo", "Coding agents — Lingvo")
    }
    MarketingDocPage(
        eyebrow = t("agents.coding_agents", "Coding agents"),
        title = t("agents.your_agent_already_has_the_files", "Your coding agent. Your localization context."),
        lede = t("agents.lingvo_speaks_model_context_protocol_an", "Give your agent the strings, glossary and style guide behind your app. Connect it here, then review its translation proposals in Lingvo."),
        entries = agentEntries(),
        footnote = t("agents.an_agent_proposes_a_person_approves", "Proposal submission and translation approval are separate actions. Check the MCP reference for each tool’s permissions, credit use and local-file behavior."),
    )
}

private fun agentEntries(): List<DocEntry> = listOf(
    DocEntry("why-lingvo", tr("agents.why_lingvo", "What Lingvo adds")) { AgentValueComparison() },
    DocEntry("release-example", tr("agents.release_example", "A release in practice")) { AgentValueExample() },
    DocEntry("when-to-use", tr("agents.when_to_use", "When it is worth adding")) { AgentValueFit() },
    DocEntry("setup", tr("agents.one_command", "Connect your agent")) {
        AgentSetup()
        DocsProse { DocsInlineLink("/docs/mcp", t("agents.full_mcp_reference", "MCP setup, tool schemas and troubleshooting")) }
    },

    DocEntry("context", tr("agents.what_it_gets_to_read", "What it gets to read")) {
        DocsProse(t("agents.the_hard_part_of_machine_translation", "An agent can read context from your repository. Lingvo also assembles the localization context stored with the project, so your client can request it for the strings and language it is working on."))
        DocsProse(t("agents.translation_brief_hands_an_agent_the", "translation_brief hands an agent the same bundle Lingvo assembles for a paid provider:"))
        DocsList(
            listOf(
                t("agents.your_style_guide_verbatim_as_the", "Your style guide, verbatim — the standing instructions you would otherwise repeat to every translator."),
                t("agents.the_glossary_terms_that_actually_occur", "The glossary terms that actually occur in the text being translated, including the ones marked do-not-translate."),
                t("agents.the_cldr_plural_categories_the_language", "The CLDR plural categories the target language uses, so the requested forms are explicit."),
                t("agents.each_strings_developer_context_its_length", "Each string's developer context, its length budget, its placeholders — and where it appears in your own source, with the line."),
                t("agents.what_the_target_locale_holds_right", "What the target locale holds right now, so a re-translation or a changes-requested fix has something to improve on."),
            ),
        )
        DocsProse(t("agents.it_is_free_read_only_and", "It is free, read-only, and spends no credits."))
    },

    DocEntry("safety", tr("agents.what_it_cannot_do", "What it cannot do")) {
        DocsProse(t("agents.the_tools_that_would_be_dangerous", "The MCP catalogue does not include a translation-approval tool or controls for members, billing, provider keys or tokens. This narrows what the Lingvo connection can do; it is not a sandbox for an agent that also has shell access."))
        DocsProse(t("agents.everything_an_agent_writes_lands_in", "The proposal tools keep suggestions separate from stored translations until a person accepts them. Other tools can update source strings or context, start machine translation, or write local files, subject to their own permissions and confirmation rules. A source push can obsolete keys when its explicit confirmation matches the removal count."))
        DocsProse(t("agents.for_a_fleet_that_runs_without", "For a fleet that runs without you, an agent connection carries its own scopes, a project allowlist, an autonomy level and a credit budget — all resolved on every single call, so demoting the person who created it narrows the agent within one request."))
    },
)
